import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import theme from '../../../theme';
import {
  getAllConversationFlowVersionsApi,
  existingConversationFlowVersionApi,
} from '../../../utils/apis';
import { useDashboard } from '../../../hooks/useDashboard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';
import AddLabelToVersionHistoryModal from '../../modals/AddLabelToVersionHistoryModal';

interface FlowHistorySidebarProps {
  onClose?: () => void;
}

// Memoized version item component
const FlowVersionItem = React.memo<{
  version: any;
  isCurrentVersion: boolean;
  onUseFlowVersion: (versionId: string) => void;
  onOpenLabelModal: (versionId: string) => void;
  isUsingFlowVersion: boolean;
}>(
  ({
    version,
    isCurrentVersion,
    onUseFlowVersion,
    onOpenLabelModal,
    isUsingFlowVersion,
  }) => {
    // Memoize time formatting
    const formattedDate = useMemo(() => {
      const versionDate = new Date(version.createdAt);

      if (isNaN(versionDate.getTime())) {
        return 'Invalid date';
      }

      // Array of abbreviated months with periods
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];

      const day = versionDate.getDate();
      const month = months[versionDate.getMonth()];
      const year = versionDate.getFullYear();

      // Format time in 12-hour format
      let hours = versionDate.getHours();
      const minutes = versionDate.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';

      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12

      const formattedMinutes = minutes.toString().padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${formattedMinutes} ${ampm}`;
    }, [version.updatedAt]);

    // Memoize event handlers
    const handleUseFlowVersion = useCallback(() => {
      onUseFlowVersion(version._id);
    }, [version._id, onUseFlowVersion]);

    const handleOpenLabelModal = useCallback(() => {
      onOpenLabelModal(version._id);
    }, [version._id, onOpenLabelModal]);

    return (
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          p: 2,
          mb: 2,
          border: `1px solid ${theme.palette.lightGray.main}`,
          '&:hover': {
            backgroundColor: theme.palette.custom.sidebarIconBg,
            cursor: 'pointer',
          },
        }}
        data-version-id={version._id}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Version Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                {/* Version Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Version {version?.version}
                  </Typography>
                  {isCurrentVersion && (
                    <Chip
                      label="In Use"
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '10px',
                        fontWeight: 500,
                        backgroundColor: theme.palette.lightGray.main,
                        color: theme.palette.text.primary,
                        '& .MuiChip-label': {
                          px: 1,
                          fontStyle: 'italic',
                        },
                      }}
                    />
                  )}
                </Box>

                {/* Timestamp */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    mb: 1,
                  }}
                >
                  {formattedDate}
                </Typography>
              </Box>
            </Box>

            {/* Commit Message */}
            {version.commitMessage && (
              <Tooltip title={version.commitMessage}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  "{version.commitMessage}"
                </Typography>
              </Tooltip>
            )}

            {/* Labels */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              {version.label?.map((label: string, labelIndex: number) => (
                <Chip
                  key={labelIndex}
                  label={label}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '10px',
                    padding: '2px 12px',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.custom.sidebarIconBg,
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              ))}
              <Tooltip title="Add Label">
                <IconButton
                  size="small"
                  onClick={handleOpenLabelModal}
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  <PlusIcon size={12} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={isUsingFlowVersion}
                onClick={handleUseFlowVersion}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  height: '28px',
                  borderRadius: '6px',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  boxShadow: 'none',
                  flex: 1,
                  '&:hover': {
                    boxShadow: 'none',
                    borderColor: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.lightGray.main,
                    color: theme.palette.text.secondary,
                  },
                }}
              >
                {isUsingFlowVersion ? 'Updating...' : 'Use'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

FlowVersionItem.displayName = 'FlowVersionItem';

const FlowHistorySidebar: React.FC<FlowHistorySidebarProps> = ({ onClose }) => {
  const { selectedAssistant } = useDashboard();
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const [flowVersions, setFlowVersions] = useState<any[]>([]);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState<boolean>(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [usingVersionId, setUsingVersionId] = useState<string | null>(null);

  // Date range logic (last 30 days)
  const dateRange = useMemo(() => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    const toDate = new Date();
    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: [
      'get-conversation-flow-versions',
      selectedAssistant?._id,
      dateRange.fromDate,
      dateRange.toDate,
    ],
    queryFn: () =>
      getAllConversationFlowVersionsApi(
        selectedAssistant?._id || '',
        dateRange.fromDate,
        dateRange.toDate
      ),
    enabled: !!selectedAssistant,
  });

  useEffect(() => {
    if (data?.data?.data) {
      setFlowVersions(data.data.data);
    }
  }, [data]);

  // Mutation for using a flow version
  const { mutate: flowVersionMutation } = useMutation({
    mutationFn: ({
      versionId,
      agentId,
    }: {
      versionId: string;
      agentId: string;
    }) => existingConversationFlowVersionApi(versionId, agentId),
    onMutate: ({ versionId }) => {
      setUsingVersionId(versionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get-conversation-flow-versions', selectedAssistant?._id],
      });
      queryClient.invalidateQueries({
        queryKey: ['conversationFlows', selectedAssistant?._id],
      });
      showAlert('Conversation flow version is now in use', 'success');
      setUsingVersionId(null);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message ||
          'Failed to use conversation flow version',
        'error'
      );
      setUsingVersionId(null);
    },
  });

  // Update handler to use the mutation
  const handleUseFlowVersion = useCallback(
    (versionId: string) => {
      if (!selectedAssistant?._id) return;
      flowVersionMutation({ versionId, agentId: selectedAssistant._id });
    },
    [selectedAssistant, flowVersionMutation]
  );

  // Memoize container styles
  const containerStyle = useMemo(
    () => ({
      width: '320px',
      height: '100%',
      backgroundColor: '#FFFFFF',
      borderLeft: `1px solid ${theme.palette.lightGray.main}`,
      display: 'flex',
      flexDirection: 'column' as const,
    }),
    []
  );

  const scrollContainerStyle = useMemo(
    () => ({
      flex: 1,
      overflowY: 'auto' as const,
      px: 2,
      '&::-webkit-scrollbar': { display: 'none' },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    }),
    []
  );

  // Handlers for modal and selection
  const handleOpenLabelModal = useCallback((versionId: string) => {
    setSelectedVersionId(versionId);
    setIsLabelModalOpen(true);
  }, []);
  const handleCloseLabelModal = useCallback(() => {
    setIsLabelModalOpen(false);
  }, []);

  if (isLoading) {
    return (
      <Box sx={containerStyle}>
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.lightGray.main}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Skeleton variant="text" width={120} height={24} />
            {onClose && (
              <IconButton size="small" onClick={onClose}>
                <XIcon size={16} />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, p: 2 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton
                variant="text"
                width="40%"
                height={16}
                sx={{ mt: 0.5 }}
              />
              <Skeleton
                variant="text"
                width="80%"
                height={14}
                sx={{ mt: 0.5 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: '12px' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={20}
                  height={20}
                  sx={{ borderRadius: '50%' }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerStyle}>
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Conversation Flow Versions
          </Typography>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <XIcon size={16} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Version List */}
      <Box sx={scrollContainerStyle}>
        {flowVersions.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              No conversation flow version history available
            </Typography>
          </Box>
        ) : (
          flowVersions.map((version: any, idx: number) => (
            <FlowVersionItem
              key={version?._id}
              version={version}
              isCurrentVersion={idx === 0}
              onUseFlowVersion={handleUseFlowVersion}
              onOpenLabelModal={handleOpenLabelModal}
              isUsingFlowVersion={usingVersionId === version._id}
            />
          ))
        )}
      </Box>

      {/* Modals (placeholders) */}
      <AddLabelToVersionHistoryModal
        open={isLabelModalOpen}
        onClose={handleCloseLabelModal}
        versionId={selectedVersionId}
        selectedView="advance"
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default React.memo(FlowHistorySidebar);
